/**
 * Street Craps Rules
 *
 * Pure rules layer for the V1 street-craps loop. Mirrors the shape of
 * CeeloRules.ts so the two games are interchangeable from the strategy
 * layer's point of view (locked architecture: GameStrategy injection).
 *
 * Design source: project_craps_loop_design.md (locked 2026-05-05).
 *
 * Zero side effects. No room/network references. Shared between client
 * (UI feedback, sidebet menu rendering) and server (authoritative
 * settlement). Odds marked TODO are placeholders pending product tuning.
 */
export function createCrapsRoll(d1, d2) {
    const total = d1 + d2;
    const isHardway = d1 === d2 && (total === 4 || total === 6 || total === 8 || total === 10);
    return { dice: [d1, d2], total, isHardway };
}
export function getCrapsRollTotal(roll) {
    return roll.total;
}
export function validateCrapsDice(dice) {
    return dice.every(d => Number.isInteger(d) && d >= 1 && d <= 6);
}
// ============================================================================
// OUTCOME PREDICATES (come-out + point)
// ============================================================================
/** 7 or 11 on come-out — shooter wins immediately. */
export function isNatural(roll) {
    return roll.total === 7 || roll.total === 11;
}
/** 2, 3, or 12 on come-out — shooter loses immediately. */
export function isCraps(roll) {
    return roll.total === 2 || roll.total === 3 || roll.total === 12;
}
export function evaluateComeOut(roll) {
    const t = roll.total;
    if (t === 7 || t === 11)
        return { kind: 'natural', total: t };
    if (t === 2 || t === 3 || t === 12)
        return { kind: 'craps', total: t };
    return { kind: 'point', point: t };
}
export function evaluatePointRoll(roll, point) {
    if (roll.total === point)
        return { kind: 'point_hit' };
    if (roll.total === 7)
        return { kind: 'seven_out' };
    return { kind: 'continue' };
}
// ============================================================================
// SIDEBET TYPES
// ============================================================================
export var CrapsSideBetType;
(function (CrapsSideBetType) {
    // Turn-level (resolve at end of shooter's turn)
    CrapsSideBetType["SHOOTER_WINS"] = "shooter_wins";
    CrapsSideBetType["SHOOTER_LOSES"] = "shooter_loses";
    CrapsSideBetType["SEVEN_OUT"] = "seven_out";
    // Multi-roll point pairs — resolve at end of point phase
    CrapsSideBetType["POINT_PAIR_4_10"] = "point_pair_4_10";
    CrapsSideBetType["POINT_PAIR_5_9"] = "point_pair_5_9";
    CrapsSideBetType["POINT_PAIR_6_8"] = "point_pair_6_8";
    // Multi-roll hardways — resolve when target total hits OR 7 hits OR easy version of target hits
    CrapsSideBetType["HARDWAY_4"] = "hardway_4";
    CrapsSideBetType["HARDWAY_6"] = "hardway_6";
    CrapsSideBetType["HARDWAY_8"] = "hardway_8";
    CrapsSideBetType["HARDWAY_10"] = "hardway_10";
    // One-roll — resolve on next dice result
    CrapsSideBetType["YO"] = "yo";
    CrapsSideBetType["SNAKE_EYES"] = "snake_eyes";
    CrapsSideBetType["BOXCARS"] = "boxcars";
    CrapsSideBetType["ANY_SEVEN"] = "any_seven";
    CrapsSideBetType["FIELD"] = "field";
    // One-roll hop (representative entry — additional combos can be registered
    // by extending CRAPS_SIDE_BETS without changing the evaluator core).
    CrapsSideBetType["HOP_5_2"] = "hop_5_2";
})(CrapsSideBetType || (CrapsSideBetType = {}));
// Odds sources: project_craps_loop_design.md design table.
// SHOOTER_WINS/LOSES (1:1) and FIELD (1:1, 2:1 on 2/12) are locked.
// HARDWAY 4/10=7:1 and 6/8=9:1 are canonical street odds.
// POINT_PAIR uses true odds against the point: 4/10=2:1, 5/9=3:2, 6/8=6:5.
// Single-roll street odds: YO=15, SE/BC=30, AnySeven=4, HOP easy=15.
// SEVEN_OUT odds are placeholder pending product tuning (TODO).
export const CRAPS_SIDE_BETS = {
    [CrapsSideBetType.SHOOTER_WINS]: {
        type: CrapsSideBetType.SHOOTER_WINS,
        label: 'Shooter Wins',
        odds: 1,
        resolution: 'turn',
        window: 'pre_come_out',
    },
    [CrapsSideBetType.SHOOTER_LOSES]: {
        type: CrapsSideBetType.SHOOTER_LOSES,
        label: 'Shooter Loses',
        odds: 1,
        resolution: 'turn',
        window: 'pre_come_out',
    },
    [CrapsSideBetType.SEVEN_OUT]: {
        type: CrapsSideBetType.SEVEN_OUT,
        label: 'Seven Out',
        odds: 1, // TODO: tune — narrower than SHOOTER_LOSES, should pay better
        resolution: 'turn',
        window: 'pre_come_out',
    },
    [CrapsSideBetType.POINT_PAIR_4_10]: {
        type: CrapsSideBetType.POINT_PAIR_4_10,
        label: 'Point Pair 4/10',
        odds: 2,
        // Per-roll place bet: wins on 4/10, loses on 7/11, survives all other
        // rolls (incl. across rounds) until one of those hits.
        resolution: 'place',
        window: 'mid_point',
        pointBucket: [4, 10],
        placeWin: [4, 10],
        placeLose: [7, 11],
    },
    [CrapsSideBetType.POINT_PAIR_5_9]: {
        type: CrapsSideBetType.POINT_PAIR_5_9,
        label: 'Point Pair 5/9',
        odds: 1.5,
        resolution: 'place',
        window: 'mid_point',
        pointBucket: [5, 9],
        placeWin: [5, 9],
        placeLose: [7, 11],
    },
    [CrapsSideBetType.POINT_PAIR_6_8]: {
        type: CrapsSideBetType.POINT_PAIR_6_8,
        label: 'Point Pair 6/8',
        odds: 1.2,
        resolution: 'place',
        window: 'mid_point',
        pointBucket: [6, 8],
        placeWin: [6, 8],
        // 6/8 hit most often, so it loses to more numbers (craps + 7 + 11).
        placeLose: [2, 3, 7, 11, 12],
    },
    [CrapsSideBetType.HARDWAY_4]: {
        type: CrapsSideBetType.HARDWAY_4,
        label: 'Hard 4',
        odds: 7,
        resolution: 'multi_roll',
        window: 'pre_come_out',
        hardwayTarget: 4,
    },
    [CrapsSideBetType.HARDWAY_6]: {
        type: CrapsSideBetType.HARDWAY_6,
        label: 'Hard 6',
        odds: 9,
        resolution: 'multi_roll',
        window: 'pre_come_out',
        hardwayTarget: 6,
    },
    [CrapsSideBetType.HARDWAY_8]: {
        type: CrapsSideBetType.HARDWAY_8,
        label: 'Hard 8',
        odds: 9,
        resolution: 'multi_roll',
        window: 'pre_come_out',
        hardwayTarget: 8,
    },
    [CrapsSideBetType.HARDWAY_10]: {
        type: CrapsSideBetType.HARDWAY_10,
        label: 'Hard 10',
        odds: 7,
        resolution: 'multi_roll',
        window: 'pre_come_out',
        hardwayTarget: 10,
    },
    [CrapsSideBetType.YO]: {
        type: CrapsSideBetType.YO,
        label: 'Yo',
        odds: 15,
        resolution: 'one_roll',
        window: 'mid_point',
        winningTotals: [11],
    },
    [CrapsSideBetType.SNAKE_EYES]: {
        type: CrapsSideBetType.SNAKE_EYES,
        label: 'Snake Eyes',
        odds: 30,
        resolution: 'one_roll',
        window: 'mid_point',
        winningTotals: [2],
    },
    [CrapsSideBetType.BOXCARS]: {
        type: CrapsSideBetType.BOXCARS,
        label: 'Boxcars',
        odds: 30,
        resolution: 'one_roll',
        window: 'mid_point',
        winningTotals: [12],
    },
    [CrapsSideBetType.ANY_SEVEN]: {
        type: CrapsSideBetType.ANY_SEVEN,
        label: 'Any 7',
        odds: 4,
        resolution: 'one_roll',
        window: 'mid_point',
        winningTotals: [7],
    },
    [CrapsSideBetType.FIELD]: {
        type: CrapsSideBetType.FIELD,
        label: 'Field',
        odds: 1, // base 1:1; 2:1 on 2/12 handled in payout
        resolution: 'one_roll',
        window: 'mid_point',
        winningTotals: [2, 3, 4, 9, 10, 11, 12],
    },
    [CrapsSideBetType.HOP_5_2]: {
        type: CrapsSideBetType.HOP_5_2,
        label: 'Hop 5-2',
        odds: 15,
        resolution: 'one_roll',
        window: 'mid_point',
        hopCombo: [5, 2],
    },
};
export function shooterWonTurn(history) {
    const k = history.finalOutcome.kind;
    return k === 'natural' || k === 'point_hit';
}
export function shooterLostTurn(history) {
    const k = history.finalOutcome.kind;
    return k === 'come_out_craps' || k === 'seven_out';
}
/**
 * Evaluate a one-roll sidebet against a single dice result.
 * Returns 'lose' for non-one-roll types so a generic dispatcher can route safely.
 */
export function evaluateCrapsSideBetForRoll(type, roll) {
    const def = CRAPS_SIDE_BETS[type];
    if (def.resolution !== 'one_roll')
        return 'lose';
    if (def.hopCombo) {
        const [a, b] = def.hopCombo;
        const [d1, d2] = roll.dice;
        return ((d1 === a && d2 === b) || (d1 === b && d2 === a)) ? 'win' : 'lose';
    }
    if (def.winningTotals?.includes(roll.total))
        return 'win';
    return 'lose';
}
export function evaluateCrapsPlaceBet(type, rollTotal) {
    const def = CRAPS_SIDE_BETS[type];
    if (def.resolution !== 'place')
        return 'no_action';
    if (def.placeWin?.includes(rollTotal))
        return 'win';
    if (def.placeLose?.includes(rollTotal))
        return 'lose';
    return 'no_action';
}
/**
 * Evaluate a multi-roll or turn-level sidebet against the full turn history.
 * Returns 'lose' for one-roll types (use evaluateCrapsSideBetForRoll instead).
 */
export function evaluateCrapsSideBetForTurn(type, history) {
    const def = CRAPS_SIDE_BETS[type];
    if (def.resolution === 'one_roll')
        return 'lose';
    switch (type) {
        case CrapsSideBetType.SHOOTER_WINS:
            return shooterWonTurn(history) ? 'win' : 'lose';
        case CrapsSideBetType.SHOOTER_LOSES:
            return shooterLostTurn(history) ? 'win' : 'lose';
        case CrapsSideBetType.SEVEN_OUT:
            // Wins only if shooter sevened out. Come-out craps loses (push could
            // be argued — locked as lose for V1; revisit during odds tuning).
            return history.finalOutcome.kind === 'seven_out' ? 'win' : 'lose';
        case CrapsSideBetType.POINT_PAIR_4_10:
        case CrapsSideBetType.POINT_PAIR_5_9:
        case CrapsSideBetType.POINT_PAIR_6_8: {
            // Push if no point ever established (come-out resolved the turn).
            if (history.point === null)
                return 'push';
            const bucket = def.pointBucket;
            if (!bucket.includes(history.point))
                return 'lose';
            return history.finalOutcome.kind === 'point_hit' ? 'win' : 'lose';
        }
        case CrapsSideBetType.HARDWAY_4:
        case CrapsSideBetType.HARDWAY_6:
        case CrapsSideBetType.HARDWAY_8:
        case CrapsSideBetType.HARDWAY_10: {
            // Hardway: win on hard target before 7 or easy target. Push if turn
            // ends without the target ever being rolled.
            const target = def.hardwayTarget;
            const allRolls = [history.comeOut.roll, ...history.pointRolls];
            for (const r of allRolls) {
                if (r.total === 7)
                    return 'lose';
                if (r.total === target)
                    return r.isHardway ? 'win' : 'lose';
            }
            return 'push';
        }
        default:
            return 'lose';
    }
}
/**
 * Per-roll hardway status. Used by the engine if it wants to surface live
 * hardway state (UI ticker) before turn end. Returns 'pending' until the
 * bet resolves.
 */
export function evaluateHardwayProgress(type, roll) {
    const target = CRAPS_SIDE_BETS[type].hardwayTarget;
    if (roll.total === 7)
        return 'lose';
    if (roll.total === target)
        return roll.isHardway ? 'win' : 'lose';
    return 'pending';
}
// ============================================================================
// PAYOUT
// ============================================================================
/**
 * Stake-inclusive payout: amount returned to the player on win
 * (their bet + winnings). 0 on lose. Equal to bet on push (refund).
 *
 * For FIELD, 2 and 12 pay 2:1 instead of the default 1:1.
 */
export function calculateCrapsSideBetPayout(type, outcome, betAmount, resolutionRoll) {
    if (outcome === 'lose')
        return 0;
    if (outcome === 'push')
        return betAmount;
    const def = CRAPS_SIDE_BETS[type];
    if (type === CrapsSideBetType.FIELD && resolutionRoll) {
        if (resolutionRoll.total === 2 || resolutionRoll.total === 12) {
            return betAmount * 3; // 2:1 + stake
        }
        return betAmount * 2; // 1:1 + stake
    }
    return betAmount * (def.odds + 1);
}
// ============================================================================
// DISPLAY HELPERS
// ============================================================================
export function getComeOutName(outcome, roll) {
    switch (outcome.kind) {
        case 'natural':
            return outcome.total === 7 ? 'Natural 7!' : 'Yo-Eleven!';
        case 'craps':
            if (outcome.total === 2)
                return 'Snake Eyes — Craps!';
            if (outcome.total === 3)
                return 'Ace-Deuce — Craps!';
            return 'Boxcars — Craps!';
        case 'point':
            return `Point is ${outcome.point}`;
    }
    // Exhaustive switch; total fallback for safety
    return `Roll ${roll.total}`;
}
export function getPointResultName(outcome, point) {
    switch (outcome.kind) {
        case 'point_hit':
            return `Made the ${point}!`;
        case 'seven_out':
            return 'Seven out!';
        case 'continue':
            return 'Roll again';
    }
}
//# sourceMappingURL=CrapsRules.js.map